import type { PluginRegistry } from '@embedpdf/react-pdf-viewer';
import type { EventProperties } from '../types/posthogTypes';

/** Single PostHog/Hotjar event for all PDF viewer feature actions. */
export const PDF_VIEWER_FEATURE_EVENT = 'PDF_Viewer_Feature';

export const PDF_VIEWER_ACTION_TYPE = {
  view: 'PDF View',
  print: 'PDF Print',
  download: 'PDF Download',
  search: 'PDF Search',
} as const;

export type PdfViewerActionType =
  (typeof PDF_VIEWER_ACTION_TYPE)[keyof typeof PDF_VIEWER_ACTION_TYPE];

export type PdfViewerActionExtras = {
  searchText?: string;
};

/**
 * Context passed from hop pages into the PDF viewer so entity-specific
 * PostHog properties (opinion / page / grid / alert) can be resolved.
 */
export type PdfViewerAnalyticsContext = {
  documentId?: string;
  filename?: string;
  fileExtn?: string;
  productID?: string;
  productName?: string;
  module?: string;
  downloadLocation?: string;
  downloadLocationUser?: string;
  /** Editorial / SpeedRead */
  pageId?: string;
  pageName?: string;
  /** Report / opinion */
  opinionID?: string;
  opinionName?: string;
  /** Grid */
  gridID?: string;
  gridName?: string;
  /** Alert */
  alertId?: string;
  alertTitle?: string;
};

interface ExportPluginInstance {
  onRequest?: (listener: (event: { documentId: string }) => void) => () => void;
}

interface PrintPluginInstance {
  onPrintRequest?: (listener: () => void) => () => void;
}

const SEARCH_DEBOUNCE_MS = 4000;

function getPluginInstance<T>(registry: PluginRegistry, pluginId: string): T | undefined {
  return registry.getPlugin(pluginId) as T | undefined;
}

function getPluginProvides<T>(registry: PluginRegistry, pluginId: string): T | undefined {
  return registry.getPlugin(pluginId)?.provides?.() as T | undefined;
}

function extractFileExtension(filename: string): string {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
    return 'pdf';
  }
  return filename.slice(lastDotIndex + 1).toLowerCase();
}

/**
 * Builds the PostHog payload for `PDF_Viewer_Feature`.
 * Only one entity pair (page / opinion / grid / alert) is included, matching
 * the resolution order used by PDFPageDownload.
 */
export function buildPdfViewerFeatureProperties(
  context: PdfViewerAnalyticsContext | null | undefined,
  actionType: PdfViewerActionType,
  extras?: PdfViewerActionExtras,
): EventProperties {
  const filename = context?.filename?.trim() || 'document.pdf';
  const fileExtn = context?.fileExtn?.trim() || extractFileExtension(filename);

  const properties: EventProperties = {
    actionType,
    filename,
    fileExtn,
    downloadLocationUser: context?.downloadLocationUser?.trim() || 'Enduser',
  };

  if (actionType === PDF_VIEWER_ACTION_TYPE.search && extras?.searchText) {
    properties.searchText = extras.searchText;
  }

  if (context?.documentId) {
    properties.documentId = context.documentId;
  }
  if (context?.productID) {
    properties.productID = context.productID;
  }
  if (context?.productName) {
    properties.productName = context.productName;
  }
  if (context?.module) {
    properties.module = context.module;
  }
  if (context?.downloadLocation) {
    properties.downloadLocation = context.downloadLocation;
  }

  // Entity-specific: page → opinion → grid → alert (same order as PDFPageDownload)
  if (context?.pageId) {
    properties.pageId = context.pageId;
    if (context.pageName) {
      properties.pageName = context.pageName;
    }
  } else if (context?.opinionID) {
    properties.opinionID = context.opinionID;
    if (context.opinionName) {
      properties.opinionName = context.opinionName;
    }
  } else if (context?.gridID) {
    properties.gridID = context.gridID;
    if (context.gridName) {
      properties.gridName = context.gridName;
    }
  } else if (context?.alertId) {
    properties.alertId = context.alertId;
    if (context.alertTitle) {
      properties.alertTitle = context.alertTitle;
    }
  }

  return properties;
}

/**
 * Parses entity IDs from a hop-page `src` URL (e.g. `/source-doc-download?opinionID=…`).
 * Used as a fallback when analyticsContext was not handed off.
 */
export function parsePdfViewerAnalyticsFromSourceHref(
  sourceHref: string | null | undefined,
): PdfViewerAnalyticsContext | null {
  if (!sourceHref?.trim()) {
    return null;
  }

  try {
    const url = new URL(sourceHref, globalThis.location.origin);
    const parameters = url.searchParams;

    const pick = (...keys: Array<string>): string | undefined => {
      for (const key of keys) {
        const value = parameters.get(key)?.trim();
        if (value && value !== '0' && value !== 'null') {
          return value;
        }
      }
      return undefined;
    };

    const filename =
      pick('downloadName', 'filename', 'fileName', 'docID', 'docId') ?? undefined;
    const documentIdFromFile = filename
      ? filename.replace(/\.[^.]+$/, '')
      : pick('docID', 'docId');

    const context: PdfViewerAnalyticsContext = {
      documentId: documentIdFromFile,
      filename,
      fileExtn: pick('extension') ?? (filename ? extractFileExtension(filename) : undefined),
      productID: pick('ProdID', 'prodID', 'prodId', 'renderStyleId', 'renderStyleID', 'erenStyleId'),
      pageId: pick('pageId', 'pageID'),
      opinionID: pick('opinionID', 'opinionId'),
      gridID: pick('gridID', 'gridId'),
      alertId: pick('alertId', 'alertID'),
    };

    const hasEntity =
      !!context.pageId || !!context.opinionID || !!context.gridID || !!context.alertId;
    const hasFile = !!context.filename || !!context.documentId;

    return hasEntity || hasFile ? context : null;
  } catch {
    return null;
  }
}

/** Merges analytics contexts; `primary` wins on non-empty fields. */
export function mergePdfViewerAnalyticsContext(
  primary: PdfViewerAnalyticsContext | null | undefined,
  fallback: PdfViewerAnalyticsContext | null | undefined,
): PdfViewerAnalyticsContext {
  const merged: PdfViewerAnalyticsContext = { ...fallback, ...primary };
  // Prefer primary non-empty strings over fallback empties already handled by spread;
  // fill gaps from fallback where primary omitted the key.
  if (!primary) {
    return { ...fallback };
  }
  if (!fallback) {
    return { ...primary };
  }
  (Object.keys(fallback) as Array<keyof PdfViewerAnalyticsContext>).forEach((key) => {
    if (merged[key] == null || merged[key] === '') {
      merged[key] = fallback[key];
    }
  });
  return merged;
}

function applyDocumentFileName(
  target: PdfViewerAnalyticsContext,
  documentName: string | null | undefined,
  extension: string | null | undefined,
): void {
  const name = documentName?.trim();
  if (!name) {
    return;
  }
  const extension_ = extension?.trim();
  target.filename = extension_ ? `${name}.${extension_}` : name;
  if (extension_) {
    target.fileExtn = extension_;
  }
}

type DocumentDetailsSources = {
  pageDocumentData?: {
    pageDocumentProfileModelList?: Array<{
      pageId: number;
      pageName?: string;
      renderStyleId?: number;
      renderStyleName?: string;
      docName?: string;
      fileExtension?: string;
    }>;
  } | null;
  opinionDocumentData?: {
    reportDocumentProfileModelList?: Array<{
      opinionId: number;
      opinionName?: string;
      renderStyleId?: number;
      renderStyleName?: string;
      docName?: string;
      extension?: string;
    }>;
  } | null;
  gridDocumentData?: {
    gridDocumentProfileModels?: Array<{
      gridId?: number;
      gridName?: string;
      renderStyleId?: number;
      renderStyleName?: string;
      docName?: string;
      extension?: string;
    }>;
  } | null;
};

/**
 * Fills missing product/entity names from the existing document-details APIs.
 * Resolution order: page → opinion → grid → alert.
 */
export function enrichPdfViewerAnalyticsContext(
  base: PdfViewerAnalyticsContext,
  sources: DocumentDetailsSources,
): PdfViewerAnalyticsContext {
  const enriched: PdfViewerAnalyticsContext = { ...base };
  const pageList = sources.pageDocumentData?.pageDocumentProfileModelList;
  const opinionList = sources.opinionDocumentData?.reportDocumentProfileModelList;
  const gridList = sources.gridDocumentData?.gridDocumentProfileModels;

  if (base.pageId && pageList && pageList.length > 0) {
    const pageDocument = pageList[0];
    enriched.pageId = String(pageDocument.pageId);
    enriched.pageName = pageDocument.pageName ?? enriched.pageName;
    enriched.productID = pageDocument.renderStyleId?.toString() ?? enriched.productID;
    enriched.productName = pageDocument.renderStyleName ?? enriched.productName;
    applyDocumentFileName(enriched, pageDocument.docName, pageDocument.fileExtension);
    enriched.module = enriched.module || 'Pages';
    enriched.downloadLocation = enriched.downloadLocation || enriched.module;
    return enriched;
  }

  if (base.opinionID && opinionList && opinionList.length > 0) {
    const opinionDocument = opinionList[0];
    enriched.opinionID = String(opinionDocument.opinionId);
    enriched.opinionName = opinionDocument.opinionName ?? enriched.opinionName;
    enriched.productID = opinionDocument.renderStyleId?.toString() ?? enriched.productID;
    enriched.productName = opinionDocument.renderStyleName ?? enriched.productName;
    applyDocumentFileName(enriched, opinionDocument.docName, opinionDocument.extension);
    enriched.module = enriched.module || 'Report';
    enriched.downloadLocation = enriched.downloadLocation || 'Report';
    return enriched;
  }

  if (base.gridID && gridList && gridList.length > 0) {
    const gridDocument = gridList[0];
    enriched.gridID = gridDocument.gridId?.toString() ?? enriched.gridID;
    enriched.gridName = gridDocument.gridName ?? enriched.gridName;
    enriched.productID = gridDocument.renderStyleId?.toString() ?? enriched.productID;
    enriched.productName = gridDocument.renderStyleName ?? enriched.productName;
    applyDocumentFileName(enriched, gridDocument.docName, gridDocument.extension);
    enriched.module = enriched.module || 'Grid';
    enriched.downloadLocation = enriched.downloadLocation || 'Grid';
    return enriched;
  }

  if (enriched.alertId) {
    enriched.module = enriched.module || 'Alerts';
    enriched.downloadLocation =
      enriched.downloadLocation || 'Alert Attachment Download';
  }

  return enriched;
}

interface DocumentManagerProvides {
  onDocumentOpened?: (
    handler: (documentState: { id: string; name?: string }) => void,
  ) => () => void;
  onDocumentError?: (handler: (event: unknown) => void) => () => void;
  onDocumentClosed?: (handler: (documentId: string) => void) => () => void;
}

/**
 * Subscribes to EmbedPDF plugin APIs for View / Print / Export / Search.
 *
 * EmbedPDF menu buttons call `commands.resolve(id).execute()` directly, so
 * `onCommandExecuted` never fires for UI clicks. We hook plugin instances instead.
 */
export function installPdfViewerAnalytics(
  registry: PluginRegistry,
  onAction: (actionType: PdfViewerActionType, extras?: PdfViewerActionExtras) => void,
): () => void {
  const cleanups: Array<() => void> = [];
  const openedDocumentIds = new Set<string>();

  const documentManager = getPluginProvides<DocumentManagerProvides>(
    registry,
    'document-manager',
  );
  if (documentManager?.onDocumentOpened) {
    const unsubscribe = documentManager.onDocumentOpened((documentState) => {
      const documentId = documentState?.id;
      if (documentId && openedDocumentIds.has(documentId)) {
        return;
      }
      if (documentId) {
        openedDocumentIds.add(documentId);
      }
      onAction(PDF_VIEWER_ACTION_TYPE.view);
    });
    cleanups.push(unsubscribe);
  }

  // Export: fires when export.download() runs (hamburger Export button).
  const exportPlugin = getPluginInstance<ExportPluginInstance>(registry, 'export');
  if (exportPlugin?.onRequest) {
    cleanups.push(
      exportPlugin.onRequest(() => {
        onAction(PDF_VIEWER_ACTION_TYPE.download);
      }),
    );
  }

  // Print: fires when the print dialog is prepared (user confirms in print modal).
  const printPlugin = getPluginInstance<PrintPluginInstance>(registry, 'print');
  if (printPlugin?.onPrintRequest) {
    cleanups.push(
      printPlugin.onPrintRequest(() => {
        onAction(PDF_VIEWER_ACTION_TYPE.print);
      }),
    );
  }

  // Search: fire when the user actually searches, including searchText.
  hookPdfSearch(registry, onAction, cleanups);

  return () => {
    cleanups.forEach((cleanup) => cleanup());
    openedDocumentIds.clear();
  };
}

/**
 * PDF Search is a committed query, not a keystroke.
 * Debounce live search; flush on panel close / cleanup so the last term is not lost.
 */
function hookPdfSearch(
  registry: PluginRegistry,
  onAction: (actionType: PdfViewerActionType, extras?: PdfViewerActionExtras) => void,
  cleanups: Array<() => void>,
): void {
  const searchApi = getPluginProvides<{
    onStateChange?: (
      handler: (event: { documentId: string; state: { query?: string } }) => void,
    ) => () => void;
    onSearchStop?: (handler: (event: { documentId: string }) => void) => () => void;
  }>(registry, 'search');
  if (!searchApi?.onStateChange) {
    return;
  }

  let pendingText = '';
  let lastCommitted = '';
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clearTimer = (): void => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const flush = (): void => {
    clearTimer();
    const searchText = pendingText.trim();
    pendingText = '';
    if (!searchText || searchText === lastCommitted) {
      return;
    }
    lastCommitted = searchText;
    onAction(PDF_VIEWER_ACTION_TYPE.search, { searchText });
  };

  const schedule = (raw: string | undefined): void => {
    const next = raw?.trim() ?? '';
    if (!next) {
      pendingText = '';
      clearTimer();
      return;
    }
    if (next === pendingText || next === lastCommitted) {
      return;
    }
    pendingText = next;
    clearTimer();
    timer = setTimeout(flush, SEARCH_DEBOUNCE_MS);
  };

  cleanups.push(
    searchApi.onStateChange((event) => {
      schedule(event.state?.query);
    }),
  );

  if (searchApi.onSearchStop) {
    cleanups.push(
      searchApi.onSearchStop(() => {
        flush();
      }),
    );
  }

  cleanups.push(() => {
    flush();
  });
}
